import { useContext, useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useNotificationFeed } from '../hooks/useNotificationFeed';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../auth/AuthContext';
import {
  NAV_BY_PERMISSION,
  ALWAYS_ON_NAV_PATHS,
  HIDDEN_NAV_PATHS,
  ADMIN_TEAM_ROLES,
  DEVELOPER_ACTIVITY_HIDDEN_ROLES,
  USER_MANAGEMENT_NAV_LABEL,
  labelForNavItem,
} from '../config/navByPermission';
import { normalizeAppRole } from '../utils/roleUtils';
import { DEFAULT_DEPARTMENT, departmentUsesBugTracker, normalizeDepartment } from '../constants/departments';
import { useDepartmentWorkflow } from '../hooks/useDepartmentWorkflow';
import { getStoredDepartmentView } from './DepartmentSwitcher';
import { applyNavOrder } from '../constants/departmentWorkflow';
import {
  getStoredNavOrder,
  NAV_ORDER_CHANGED_EVENT,
} from '../utils/navOrderPreference';
import { useNavItems } from './NavItemsContext';
import { COLORS } from '../constants/ui';
import {
  FiBriefcase, FiAlertCircle, FiUsers, FiPieChart, FiGitBranch,
  FiTrendingUp, FiDatabase, FiCheckCircle, FiBell, FiSettings, FiCalendar,
  FiDollarSign, FiLogOut, FiChevronLeft, FiChevronRight, FiList, FiCreditCard, FiBook,
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';

/** Mirrors backend admin ROLE_SEED nav keys — sidebar fallback before matrix loads (superadmin only). */
/** Every role sees "My Tasks" (same route); testers land on the My Bugs tab (?tab=bugs). */
function myTasksNavTo(roleLower) {
  return roleLower === 'tester' ? '/my-tasks?tab=bugs' : '/my-tasks';
}

function ensureMyTasksNav(items, roleLower = '') {
  const myTasksItem = {
    label: 'My Tasks',
    path: '/my-tasks',
    to: myTasksNavTo(roleLower),
    icon: FiCheckCircle,
  };
  const existingIdx = items.findIndex((item) => item.path === '/my-tasks');
  if (existingIdx >= 0) {
    const next = [...items];
    next[existingIdx] = { ...next[existingIdx], ...myTasksItem };
    return next;
  }
  const sprintIdx = items.findIndex((item) => item.path === '/sprint-board');
  const teamIdx = items.findIndex((item) => item.path === '/team-activity');
  const insertAt = teamIdx >= 0 ? teamIdx + 1 : sprintIdx >= 0 ? sprintIdx + 1 : items.length;
  return [...items.slice(0, insertAt), myTasksItem, ...items.slice(insertAt)];
}

/** Admin/superadmin always get Team nav after Team Activity. */
function ensureAdminTeamNav(items, roleLower) {
  if (!ADMIN_TEAM_ROLES.has(roleLower)) return items;
  const teamNav = {
    label: USER_MANAGEMENT_NAV_LABEL,
    path: '/users-management',
    to: '/users-management',
    icon: FiUsers,
  };
  const existingIdx = items.findIndex((item) => item.path === '/users-management');
  if (existingIdx >= 0) {
    const next = [...items];
    next[existingIdx] = { ...next[existingIdx], ...teamNav };
    return next;
  }
  const teamActivityIdx = items.findIndex((item) => item.path === '/team-activity');
  const insertAt = teamActivityIdx >= 0 ? teamActivityIdx + 1 : items.length;
  return [...items.slice(0, insertAt), teamNav, ...items.slice(insertAt)];
}

const Sidebar = ({
  collapsed,
  setCollapsed,
  isMobile = false,
  drawerOpen = false,
  onCloseDrawer,
}) => {
  const { role, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const currentRole = normalizeAppRole(role);
  const [workspaceEnabled, setWorkspaceEnabled] = useState(true);
  const userDepartment = normalizeDepartment(user?.department || DEFAULT_DEPARTMENT);
  const departmentView = getStoredDepartmentView();
  const effectiveDepartment = normalizeDepartment(departmentView || userDepartment);
  const {
    departmentUsesSprints: deptUsesSprints,
    getNavConfig,
    getNavOrder,
    navConfigLoading,
  } = useDepartmentWorkflow();
  const showDailyNotebookNav = !deptUsesSprints(effectiveDepartment);
  const deptNavConfig = getNavConfig(effectiveDepartment);
  const deptNavOrder = getNavOrder(effectiveDepartment);

  // The viewer's own order, re-read when they change it on Account Settings.
  const [personalNavOrder, setPersonalNavOrder] = useState(() =>
    getStoredNavOrder(effectiveDepartment),
  );
  useEffect(() => {
    const sync = () => setPersonalNavOrder(getStoredNavOrder(effectiveDepartment));
    sync();
    window.addEventListener(NAV_ORDER_CHANGED_EVENT, sync);
    // 'storage' covers the same account in another tab.
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(NAV_ORDER_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [effectiveDepartment]);
  const { setNavItems } = useNavItems();

  /**
   * Unread count for the Notifications nav badge. Shares the ["notifications"]
   * key prefix that the Notifications page invalidates after mark-as-read, so
   * the badge clears itself without extra wiring. Polls because notifications
   * arrive from other people's actions, not this tab's.
   */
  const { unread: unreadNotifications } = useNotificationFeed({ enabled: !!user?.id });

  useEffect(() => {
    const canCheckBitbucket = ['superadmin', 'admin'].includes(currentRole);
    if (!canCheckBitbucket) {
      setWorkspaceEnabled(false);
      return;
    }
    api.get('/bitbucket/admin/config')
      .then((res) => {
        setWorkspaceEnabled(res.data.workspace_enabled !== false);
      })
      .catch(() => {
        setWorkspaceEnabled(false);
      });
  }, [currentRole]);

  const handleLogout = () => {
    toast.success('Logged out successfully');
    logout();
    navigate('/');
  };

  const menuConfig = {
    superadmin: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'All Projects',   path: '/projects',           icon: FiBriefcase },
      { label: 'Budget',         path: '/budget',             icon: FiDollarSign },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiAlertCircle },
      { label: 'Team Activity',  path: '/team-activity',      icon: FiTrendingUp },
      { label: USER_MANAGEMENT_NAV_LABEL, path: '/users-management', icon: FiUsers },
      { label: 'Bitbucket',      path: '/bitbucket-admin',    icon: FiDatabase },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    admin: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'All Projects',   path: '/projects',           icon: FiBriefcase },
      { label: 'Budget',         path: '/budget',             icon: FiDollarSign },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiAlertCircle },
      { label: 'Team Activity',  path: '/team-activity',      icon: FiTrendingUp },
      { label: USER_MANAGEMENT_NAV_LABEL, path: '/users-management', icon: FiUsers },
      { label: 'Bitbucket',      path: '/bitbucket-admin',    icon: FiDatabase },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    manager: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'My Projects',    path: '/projects',           icon: FiBriefcase },
      { label: 'Budget',         path: '/budget',             icon: FiDollarSign },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiAlertCircle },
      { label: 'Team Activity',  path: '/team-activity',      icon: FiTrendingUp },
      { label: USER_MANAGEMENT_NAV_LABEL, path: '/users-management', icon: FiUsers },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    product_manager: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'My Projects',    path: '/projects',           icon: FiBriefcase },
      { label: 'Budget',         path: '/budget',             icon: FiDollarSign },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiAlertCircle },
      { label: 'Team Activity',  path: '/team-activity',      icon: FiTrendingUp },
      { label: USER_MANAGEMENT_NAV_LABEL, path: '/users-management', icon: FiUsers },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    product: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'My Projects',    path: '/projects',           icon: FiBriefcase },
      { label: 'Budget',         path: '/budget',             icon: FiDollarSign },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiAlertCircle },
      { label: 'Team Activity',  path: '/team-activity',      icon: FiTrendingUp },
      { label: USER_MANAGEMENT_NAV_LABEL, path: '/users-management', icon: FiUsers },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    analyst: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'My Projects',    path: '/projects',           icon: FiBriefcase },
      { label: 'Budget',         path: '/budget',             icon: FiDollarSign },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiAlertCircle },
      { label: 'Team Activity',  path: '/team-activity',      icon: FiTrendingUp },
      { label: USER_MANAGEMENT_NAV_LABEL, path: '/users-management', icon: FiUsers },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    lead: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'My Projects',    path: '/projects',           icon: FiBriefcase },
      { label: 'Budget',         path: '/budget',             icon: FiDollarSign },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiAlertCircle },
      { label: 'Team Activity',  path: '/team-activity',      icon: FiTrendingUp },
      { label: USER_MANAGEMENT_NAV_LABEL, path: '/users-management', icon: FiUsers },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    hod: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'My Projects',    path: '/projects',           icon: FiBriefcase },
      { label: 'Budget',         path: '/budget',             icon: FiDollarSign },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiAlertCircle },
      { label: 'Team Activity',  path: '/team-activity',      icon: FiTrendingUp },
      { label: USER_MANAGEMENT_NAV_LABEL, path: '/users-management', icon: FiUsers },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    designer: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiList },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    devops: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiList },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    developer: [
      { label: 'Dashboard',          path: '/dashboard',          icon: FiPieChart },
      { label: 'Expense Claims',     path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',       path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',             path: '/issues',             icon: FiList },
      { label: 'Developer Activity', path: '/developer-activity', icon: FiTrendingUp },
      { label: 'My Tasks',           path: '/my-tasks',           icon: FiCheckCircle },
    ],
    tester: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiAlertCircle },
      { label: 'My Tasks',       path: '/my-tasks',           to: '/my-tasks?tab=bugs', icon: FiCheckCircle },
    ],
    qa: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiAlertCircle },
      { label: 'My Tasks',       path: '/my-tasks',           to: '/my-tasks?tab=bugs', icon: FiCheckCircle },
    ],
    member: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Issues',         path: '/issues',             icon: FiList },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    hr: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'Team Activity',  path: '/team-activity',      icon: FiTrendingUp },
      { label: USER_MANAGEMENT_NAV_LABEL, path: '/users-management', icon: FiUsers },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    sales_marketing: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    content_marketer: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    marketing_designer: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    trainee: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    marketing_manager: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'My Projects',    path: '/projects',           icon: FiBriefcase },
      { label: 'Budget',         path: '/budget',             icon: FiDollarSign },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Team Activity',  path: '/team-activity',      icon: FiTrendingUp },
      { label: USER_MANAGEMENT_NAV_LABEL, path: '/users-management', icon: FiUsers },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    brand_manager: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'My Projects',    path: '/projects',           icon: FiBriefcase },
      { label: 'Budget',         path: '/budget',             icon: FiDollarSign },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'Team Activity',  path: '/team-activity',      icon: FiTrendingUp },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
    pr_manager: [
      { label: 'Dashboard',      path: '/dashboard',          icon: FiPieChart },
      { label: 'My Projects',    path: '/projects',           icon: FiBriefcase },
      { label: 'Budget',         path: '/budget',             icon: FiDollarSign },
      { label: 'Expense Claims', path: '/expense-claims',     icon: FiCreditCard },
      { label: 'Sprint Board',   path: '/sprint-board',       icon: FiGitBranch },
      { label: 'My Tasks',       path: '/my-tasks',           icon: FiCheckCircle },
    ],
  };

  const permList = user?.permissions;
  const usePermNav = Array.isArray(permList) && permList.length > 0;

  let filteredNavItems;
  if (usePermNav) {
    const navKeySet = new Set(permList);
    filteredNavItems = NAV_BY_PERMISSION.filter((item) => {
      if (ALWAYS_ON_NAV_PATHS.has(item.path)) return true;
      if (ADMIN_TEAM_ROLES.has(currentRole) && item.path === '/users-management') return true;
      if (item.permission) return navKeySet.has(item.permission);
      if (Array.isArray(item.anyPermission) && item.anyPermission.length > 0) {
        return item.anyPermission.some((k) => navKeySet.has(k));
      }
      return true;
    }).map((item) => ({
      path: item.path,
      to: item.path === '/my-tasks' ? myTasksNavTo(currentRole) : item.path,
      label: labelForNavItem(item, currentRole),
      icon: item.Icon,
    }));
  } else {
    filteredNavItems = menuConfig[currentRole] || [];
    if (!filteredNavItems.some((item) => item.path === '/notifications')) {
      filteredNavItems = [
        ...filteredNavItems,
        { label: 'Notifications', path: '/notifications', icon: FiBell },
      ];
    }
    if (!filteredNavItems.some((item) => item.path === '/team-leave')) {
      const notifIdx = filteredNavItems.findIndex((item) => item.path === '/notifications');
      const leaveItem = { label: 'Leave calendar', path: '/team-leave', icon: FiCalendar };
      if (notifIdx >= 0) {
        filteredNavItems = [
          ...filteredNavItems.slice(0, notifIdx + 1),
          leaveItem,
          ...filteredNavItems.slice(notifIdx + 1),
        ];
      } else {
        filteredNavItems = [...filteredNavItems, leaveItem];
      }
    }
  }

  filteredNavItems = ensureMyTasksNav(filteredNavItems, currentRole);
  filteredNavItems = ensureAdminTeamNav(filteredNavItems, currentRole);

  filteredNavItems = filteredNavItems.filter((item) => !HIDDEN_NAV_PATHS.has(item.path));

  // Developer Activity is a personal commit/PR feed — it's for the people
  // doing the work, not for admins or managers overseeing it. (Superadmin is
  // granted every permission automatically, so this has to be filtered here
  // rather than through role_permissions.)
  if (DEVELOPER_ACTIVITY_HIDDEN_ROLES.has(currentRole)) {
    filteredNavItems = filteredNavItems.filter((item) => item.path !== '/developer-activity');
  }

  if (!departmentUsesBugTracker(userDepartment)) {
    filteredNavItems = filteredNavItems.filter((item) => item.path !== '/issues');
  }

  if (!showDailyNotebookNav) {
    filteredNavItems = filteredNavItems.filter((item) => item.path !== '/daily-notebook');
  } else if (!filteredNavItems.some((item) => item.path === '/daily-notebook')) {
    const boardIdx = filteredNavItems.findIndex((item) => item.path === '/sprint-board');
    const notebookItem = { label: 'Daily Notebook', path: '/daily-notebook', icon: FiBook };
    if (boardIdx >= 0) {
      filteredNavItems = [
        ...filteredNavItems.slice(0, boardIdx + 1),
        notebookItem,
        ...filteredNavItems.slice(boardIdx + 1),
      ];
    } else {
      filteredNavItems = [...filteredNavItems, notebookItem];
    }
  }

  // Hide Bitbucket link if workspace token is disabled (OAuth-only mode)
  if (!workspaceEnabled) {
    filteredNavItems = filteredNavItems.filter(item => item.path !== '/bitbucket-admin');
  }

  /**
   * Department overrides from Settings → Departments: hide and rename.
   *
   * Deliberately last, and deliberately subtractive — it filters the list the
   * permission rules already produced, so a department setting can never
   * surface a route this user's role wasn't granted. It is presentation only;
   * the server still authorises every request.
   *
   * Skipped while the config is loading, so a slow request can't blank the
   * sidebar on first paint. Paths absent from the config (Settings, Bitbucket,
   * Notifications, User Management) pass through untouched.
   */
  if (!navConfigLoading && deptNavConfig) {
    filteredNavItems = filteredNavItems
      .filter((item) => deptNavConfig[item.path]?.visible !== false)
      .map((item) => {
        const override = deptNavConfig[item.path];
        return override?.label ? { ...item, label: override.label } : item;
      });

    // Department order, then the person's own — so a personal arrangement wins
    // for them while everyone else keeps the department's. Both only permute
    // the list above; neither can add an entry back.
    filteredNavItems = applyNavOrder(filteredNavItems, deptNavOrder);
    if (personalNavOrder) {
      filteredNavItems = applyNavOrder(filteredNavItems, personalNavOrder);
    }
  }

  // Publish the finished list so the command palette offers exactly the
  // destinations this user can open — no second copy of the rules above.
  // Keyed on paths so an identical list doesn't trigger a state update loop.
  const navSignature = filteredNavItems.map((i) => i.to || i.path).join('|');
  useEffect(() => {
    setNavItems(filteredNavItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navSignature, setNavItems]);

  return (
    <motion.div
      initial={{ x: -280 }}
      /**
       * Two modes. On desktop the rail is always visible and only its width
       * changes. On a phone it keeps full width and slides off the left edge
       * instead — a 280px rail on a 375px screen would leave nothing behind it.
       *
       * The width has to be animated here rather than styled in CSS: it's a
       * Framer prop, so a stylesheet could never override it.
       */
      animate={
        isMobile
          ? { x: drawerOpen ? 0 : -288, width: 280 }
          : { x: 0, width: collapsed ? 80 : 280 }
      }
      transition={{ type: "spring", stiffness: 280, damping: 28 }}
      aria-hidden={isMobile && !drawerOpen ? "true" : undefined}
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        height: "100vh",
        background: COLORS.accentInk,
        borderRight: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
        zIndex: 1200,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        // Off-canvas shouldn't swallow taps meant for the page beneath it.
        pointerEvents: isMobile && !drawerOpen ? "none" : "auto",
      }}
    >
      {/* Header */}
      <div style={{
        padding: collapsed ? '1.25rem 0.75rem' : '1.25rem 1rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        minHeight: '72px',
      }}>
        <AnimatePresence mode="wait">
          {!collapsed ? (
            <motion.div
              key="expanded"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}
            >
              <div style={{
                background: '#fff',
                borderRadius: 10,
                padding: '4px 10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              }}>
                <img 
                  src="/company-logo.png" 
                  alt="Company Logo" 
                  style={{ height: 36, width: 'auto', objectFit: 'contain' }} 
                />
              </div>
              <span style={{ 
                color: '#fff', 
                fontSize: '1rem', 
                fontWeight: '700',
                background: 'linear-gradient(135deg, #F1A028 0%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                whiteSpace: 'nowrap',
              }}>
                Team Space
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="collapsed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15 }}
              style={{
                background: '#fff',
                borderRadius: 8,
                padding: '3px 6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
              }}
            >
              <img 
                src="/company-logo.png" 
                alt="Logo" 
                style={{ height: 28, width: 'auto', objectFit: 'contain' }}
              />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Collapsing to a 80px rail makes no sense once it's a drawer —
            on mobile this is a plain close instead. */}
        {isMobile ? (
          <button
            onClick={() => onCloseDrawer?.()}
            aria-label="Close menu"
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.85)',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginLeft: '8px',
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        ) : !collapsed ? (
          <button
            onClick={() => setCollapsed(true)}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.7)',
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginLeft: '8px',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'; }}
          >
            <FiChevronLeft size={16} />
          </button>
        ) : null}
      </div>

      {/* Expand button when collapsed - shown at top of nav */}
      {collapsed && !isMobile && (
        <div style={{ padding: '0.75rem', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={() => setCollapsed(false)}
            style={{
              background: 'rgba(241, 160, 40, 0.15)',
              border: '1px solid rgba(241, 160, 40, 0.25)',
              color: '#F1A028',
              width: '40px',
              height: '32px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(241, 160, 40, 0.25)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(241, 160, 40, 0.15)'; }}
          >
            <FiChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Navigation - Using filteredNavItems */}
      <nav style={{ flex: 1, padding: '1.5rem 1rem', overflowY: 'auto' }}>
        {filteredNavItems.map((item) => {
          const path = item.to || item.path;
          // Only the Notifications row carries a count today; keeping the
          // lookup keyed off the path means adding another badged row later
          // is a one-line change rather than a new branch here.
          const badgeCount = path === '/notifications' ? unreadNotifications : 0;
          return (
          <NavLink
            key={path}
            to={path}
            style={({ isActive }) => ({
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem 1.25rem',
              marginBottom: '0.5rem',
              borderRadius: '12px',
              color: isActive ? COLORS.accentInk : 'rgba(255, 255, 255, 0.8)',
              textDecoration: 'none',
              transition: 'all 0.3s ease',
              background: isActive ? '#F1A028' : 'transparent',
              fontWeight: isActive ? '600' : '500',
            })}
          >
            <span style={{ position: 'relative', display: 'inline-flex', minWidth: '24px' }}>
              <item.icon style={{ fontSize: '1.25rem' }} />
              {/* Collapsed rail has no room for a label, so the count
                  collapses to a dot pinned to the icon instead. */}
              {badgeCount > 0 && collapsed && (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -4,
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    background: '#ef4444',
                    border: '2px solid #2C63AE',
                  }}
                />
              )}
            </span>
            {!collapsed && <span>{item.label}</span>}
            {badgeCount > 0 && !collapsed && (
              <span
                aria-label={`${badgeCount} unread`}
                style={{
                  marginLeft: 'auto',
                  minWidth: 20,
                  height: 20,
                  padding: '0 6px',
                  borderRadius: 999,
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {badgeCount > 99 ? '99+' : badgeCount}
              </span>
            )}
          </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ padding: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        
        {/* User Info + Settings - Combined clickable area */}
        <NavLink
          to="/account-settings"
          style={({ isActive }) => ({
            display: 'flex',
            alignItems: 'center',
            gap: collapsed ? 0 : '0.75rem',
            padding: collapsed ? '0.75rem' : '0.85rem',
            marginBottom: '0.75rem',
            background: isActive ? 'rgba(241, 160, 40, 0.15)' : 'rgba(255, 255, 255, 0.05)',
            border: `1px solid ${isActive ? 'rgba(241, 160, 40, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`,
            borderRadius: '12px',
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            justifyContent: collapsed ? 'center' : 'flex-start',
          })}
        >
          <div style={{
            width: collapsed ? 32 : 36,
            height: collapsed ? 32 : 36,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #F1A028 0%, #d4890f 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <FiSettings size={collapsed ? 16 : 18} color={COLORS.accentInk} />
          </div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <div style={{ 
                fontSize: '0.7rem',
                color: '#F1A028',
                textTransform: 'uppercase',
                fontWeight: 700,
                letterSpacing: '0.05em'
              }}>
                {role}
              </div>
              <div style={{ 
                fontWeight: 600, 
                fontSize: '0.85rem', 
                color: '#fff',
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap' 
              }}>
                {user?.email || 'User'}
              </div>
            </div>
          )}
        </NavLink>

        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            padding: '0.7rem',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#fca5a5',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: 600,
          }}
        >
          <FiLogOut size={16} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </motion.div>
  );
};

export default Sidebar;